import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import Collapse from "@material-ui/core/Collapse";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import SendIcon from "@material-ui/icons/Send";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

const CATALOG_EXPANDED_KEY = "contactDrawer:catalogSheetsExpanded";
const NO_CATALOG_VARIATION = "__no_catalog_variation__";
const imageCache = new Map();

const useStyles = makeStyles(theme => ({
	panel: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},
	row: {
		display: "flex",
		gap: theme.spacing(1),
		alignItems: "center",
	},
	currencyField: {
		width: 82,
		flexShrink: 0,
	},
	status: {
		color: theme.palette.text.secondary,
		fontSize: 12,
	},
	statusSuccess: {
		color: theme.palette.success.main,
	},
	statusError: {
		color: theme.palette.error.main,
	},
	empty: {
		padding: theme.spacing(2),
		color: theme.palette.text.secondary,
		textAlign: "center",
	},
	searchWrapper: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1),
	},
	searchIcon: {
		color: theme.palette.text.secondary,
	},
	card: {
		height: "100%",
		padding: theme.spacing(1),
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.75),
	},
	imageBox: {
		height: 250,
		background: "#fff",
		border: "1px solid rgba(0, 0, 0, 0.08)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		overflow: "hidden",
	},
	image: {
		width: "100%",
		height: "100%",
		objectFit: "contain",
	},
	productName: {
		fontWeight: 600,
		wordBreak: "break-word",
	},
	price: {
		color: theme.palette.success.main,
		fontWeight: 600,
	},
	oldPrice: {
		textDecoration: "line-through",
		color: theme.palette.text.secondary,
		marginRight: theme.spacing(0.5),
	},
	attributes: {
		padding: theme.spacing(0.75),
		background: theme.palette.action.hover,
	},
	variations: {
		padding: theme.spacing(0.75),
		background: theme.palette.action.hover,
	},
	quantityOffers: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.75),
	},
	quantityOfferBox: {
		border: "1px solid rgba(0, 0, 0, 0.12)",
		borderRadius: 4,
		overflow: "hidden",
		background: "#fff",
	},
	quantityOfferBoxSelected: {
		borderColor: theme.palette.primary.main,
		boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`,
	},
	quantityOfferButton: {
		width: "100%",
		border: 0,
		background: "transparent",
		padding: theme.spacing(1),
		cursor: "pointer",
		textAlign: "left",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: theme.spacing(1),
	},
	quantityOfferTitle: {
		fontWeight: 600,
	},
	quantityOfferSavings: {
		display: "inline-block",
		marginTop: 4,
		padding: "2px 8px",
		borderRadius: 12,
		background: "#000",
		color: "#fff",
		fontSize: 11,
	},
	quantityOfferPrice: {
		textAlign: "right",
		fontWeight: 600,
		color: theme.palette.primary.main,
		whiteSpace: "nowrap",
	},
	quantityOfferOldPrice: {
		display: "block",
		color: theme.palette.text.secondary,
		textDecoration: "line-through",
		fontSize: 12,
	},
	quantityOfferVariations: {
		padding: theme.spacing(0.75, 1),
		background: theme.palette.action.hover,
		borderTop: "1px solid rgba(0, 0, 0, 0.08)",
	},
	quantityOfferVariationRow: {
		display: "flex",
		gap: theme.spacing(1),
		alignItems: "center",
		marginTop: theme.spacing(0.5),
	},
	quantityOfferIndex: {
		width: 24,
		height: 24,
		borderRadius: "50%",
		background: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 12,
		fontWeight: 600,
		flexShrink: 0,
	},
	variationControl: {
		marginTop: theme.spacing(0.5),
	},
	attributeLine: {
		display: "flex",
		gap: theme.spacing(0.5),
		wordBreak: "break-word",
	},
	progress: {
		marginRight: theme.spacing(1),
	},
	chips: {
		display: "flex",
		gap: theme.spacing(0.5),
		flexWrap: "wrap",
	},
}));

const normalizeImageUrl = value => {
	if (!value) return "";
	const url = String(value);
	if (!url.includes("drive.google.com")) return url;

	const pathFileId = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
	if (pathFileId) {
		return `https://drive.google.com/uc?export=view&id=${pathFileId[1]}`;
	}

	try {
		const parsedUrl = new URL(url);
		const queryFileId = parsedUrl.searchParams.get("id");
		if (queryFileId) {
			return `https://drive.google.com/uc?export=view&id=${queryFileId}`;
		}
	} catch (err) {
		return url;
	}

	return url;
};

const getVariationOptions = product => {
	const options = {};
	(product.variations || []).forEach(variation => {
		Object.entries(variation.attributes || {}).forEach(([attribute, value]) => {
			if (!value) return;
			if (!options[attribute]) options[attribute] = new Set();
			options[attribute].add(value);
		});
	});

	return Object.entries(options).reduce((acc, [attribute, values]) => {
		acc[attribute] = Array.from(values);
		return acc;
	}, {});
};

const variationMatchesSelections = (variation, selectedAttributes = {}, ignoredAttribute = "") =>
	Object.entries(selectedAttributes).every(([attribute, value]) => {
		if (!value || attribute === ignoredAttribute) return true;
		return variation.attributes?.[attribute] === value;
	});

const getAvailableVariationOptions = (product, selectedAttributes = {}) => {
	const baseOptions = getVariationOptions(product);

	return Object.keys(baseOptions).reduce((acc, attribute) => {
		const values = new Set();

		(product.variations || []).forEach(variation => {
			if (!variationMatchesSelections(variation, selectedAttributes, attribute)) return;

			const value = variation.attributes?.[attribute];
			if (value) values.add(value);
		});

		acc[attribute] = Array.from(values);
		return acc;
	}, {});
};

const normalizeVariationSelections = (product, selectedAttributes = {}) => {
	const variationOptions = getVariationOptions(product);
	let nextSelections = Object.keys(variationOptions).reduce((acc, attribute) => {
		if (selectedAttributes[attribute]) acc[attribute] = selectedAttributes[attribute];
		return acc;
	}, {});
	let changed = Object.keys(nextSelections).length !== Object.keys(selectedAttributes).filter(
		attribute => selectedAttributes[attribute]
	).length;

	for (let index = 0; index < Object.keys(variationOptions).length; index += 1) {
		let didUpdate = false;
		const availableOptions = getAvailableVariationOptions(product, nextSelections);

		Object.entries(availableOptions).forEach(([attribute, values]) => {
			if (nextSelections[attribute] && !values.includes(nextSelections[attribute])) {
				delete nextSelections[attribute];
				changed = true;
				didUpdate = true;
				return;
			}

			if (!nextSelections[attribute] && values.length === 1) {
				nextSelections[attribute] = values[0];
				changed = true;
				didUpdate = true;
			}
		});

		if (!didUpdate) break;
	}

	return {
		selectedAttributes: nextSelections,
		availableOptions: getAvailableVariationOptions(product, nextSelections),
		changed,
	};
};

const areSelectionsEqual = (left = {}, right = {}) => {
	const leftKeys = Object.keys(left).filter(key => left[key]);
	const rightKeys = Object.keys(right).filter(key => right[key]);

	return leftKeys.length === rightKeys.length && leftKeys.every(key => left[key] === right[key]);
};

const findMatchingVariation = (product, selectedAttributes = {}) => {
	const requiredAttributes = Object.keys(getVariationOptions(product));
	if (!requiredAttributes.length) return null;

	const hasAllSelections = requiredAttributes.every(attribute => selectedAttributes[attribute]);
	if (!hasAllSelections) return null;

	return (product.variations || []).find(variation =>
		requiredAttributes.every(
			attribute => variation.attributes?.[attribute] === selectedAttributes[attribute]
		)
	);
};

const CatalogImage = ({ src, alt, classes }) => {
	const [imageSrc, setImageSrc] = useState("");
	const [failed, setFailed] = useState(false);
	const normalizedSrc = useMemo(() => normalizeImageUrl(src), [src]);

	useEffect(() => {
		let active = true;

		setFailed(false);
		setImageSrc("");

		if (!normalizedSrc) return undefined;

		if (normalizedSrc.startsWith("data:")) {
			setImageSrc(normalizedSrc);
			return undefined;
		}

		const cachedImage = imageCache.get(normalizedSrc);
		if (cachedImage) {
			setImageSrc(cachedImage);
			return undefined;
		}

		api
			.get("/catalog-sheets/image", {
				params: { url: normalizedSrc },
				responseType: "blob",
			})
			.then(({ data }) => {
				if (!active) return;
				const objectUrl = URL.createObjectURL(data);
				imageCache.set(normalizedSrc, objectUrl);
				setImageSrc(objectUrl);
			})
			.catch(() => {
				if (active) setFailed(true);
			});

		return () => {
			active = false;
		};
	}, [normalizedSrc]);

	if (!normalizedSrc || failed) {
		return <Typography color="textSecondary">Sin imagen</Typography>;
	}

	if (!imageSrc) {
		return <CircularProgress size={18} />;
	}

	return (
		<img
			className={classes.image}
			src={imageSrc}
			alt={alt}
			onError={() => setFailed(true)}
		/>
	);
};

const CatalogSheetsPanel = () => {
	const classes = useStyles();
	const { ticketId } = useParams();
	const [expanded, setExpanded] = useState(() => {
		const saved = localStorage.getItem(CATALOG_EXPANDED_KEY);
		return saved === null ? true : saved === "true";
	});
	const [spreadsheetId, setSpreadsheetId] = useState("");
	const [sheetName, setSheetName] = useState("");
	const [currencySymbol, setCurrencySymbol] = useState("$");
	const [products, setProducts] = useState([]);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [sendingProductId, setSendingProductId] = useState("");
	const [selectedVariations, setSelectedVariations] = useState({});
	const [selectedQuantityOffers, setSelectedQuantityOffers] = useState({});
	const [selectedQuantityOfferVariations, setSelectedQuantityOfferVariations] = useState({});
	const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
	const [status, setStatus] = useState({
		text: "Configura un Google Sheet para ver el catalogo.",
		type: "info",
	});

	const updateStatus = (text, type = "info") => setStatus({ text, type });

	const handleToggleExpanded = () => {
		setExpanded(prev => {
			const next = !prev;
			localStorage.setItem(CATALOG_EXPANDED_KEY, String(next));
			return next;
		});
	};

	const formatCurrency = value => `${currencySymbol || "$"}${value}`;

	const formatPrice = product => {
		if (product.type === "variable" && product.minPrice !== undefined) {
			if (product.minSalePrice && product.maxSalePrice && product.minRegularPrice) {
				const regularRange =
					product.minRegularPrice === product.maxRegularPrice
						? formatCurrency(product.minRegularPrice)
						: `${formatCurrency(product.minRegularPrice)} - ${formatCurrency(product.maxRegularPrice)}`;
				const saleRange =
					product.minSalePrice === product.maxSalePrice
						? formatCurrency(product.minSalePrice)
						: `${formatCurrency(product.minSalePrice)} - ${formatCurrency(product.maxSalePrice)}`;

				return (
					<>
						<span className={classes.oldPrice}>{regularRange}</span>
						<br />
						{saleRange}
					</>
				);
			}

			return product.minPrice === product.maxPrice
				? formatCurrency(product.minPrice)
				: `${formatCurrency(product.minPrice)} - ${formatCurrency(product.maxPrice)}`;
		}

		if (product.salePrice && product.price && product.salePrice < product.price) {
			return (
				<>
					<span className={classes.oldPrice}>{formatCurrency(product.price)}</span>
					{formatCurrency(product.salePrice)}
				</>
			);
		}

		const price = product.salePrice || product.price || "";
		return price ? formatCurrency(price) : "Sin precio";
	};

	const getProductPriceText = product => {
		if (product.type === "variable" && product.minPrice !== undefined) {
			if (product.minSalePrice && product.maxSalePrice && product.minRegularPrice) {
				const regularRange =
					product.minRegularPrice === product.maxRegularPrice
						? formatCurrency(product.minRegularPrice)
						: `${formatCurrency(product.minRegularPrice)} - ${formatCurrency(product.maxRegularPrice)}`;
				const saleRange =
					product.minSalePrice === product.maxSalePrice
						? formatCurrency(product.minSalePrice)
						: `${formatCurrency(product.minSalePrice)} - ${formatCurrency(product.maxSalePrice)}`;
				return `Antes: ${regularRange}\nAhora: ${saleRange}`;
			}

			return product.minPrice === product.maxPrice
				? formatCurrency(product.minPrice)
				: `${formatCurrency(product.minPrice)} - ${formatCurrency(product.maxPrice)}`;
		}

		return formatItemPrice(product);
	};

	const formatItemPrice = item => {
		if (!item) return "";
		if (item.salePrice && item.price && item.salePrice < item.price) {
			return `${formatCurrency(item.salePrice)} (antes ${formatCurrency(item.price)})`;
		}
		const price = item.salePrice || item.price || "";
		return price ? formatCurrency(price) : "";
	};

	const getQuantityOffers = (product, selectedVariation = null) =>
		selectedVariation?.quantityOffers?.length
			? selectedVariation.quantityOffers
			: product.quantityOffers || [];

	const formatQuantityOffersText = offers =>
		(offers || [])
			.map(
				offer =>
					`Pack x${offer.quantity}: ${formatCurrency(offer.packPrice)} (${formatCurrency(
						offer.pricePerUnit
					)} c/u)`
			)
			.join("\n");

	const getQuantityOfferBasePrice = (product, selectedVariation = null) =>
		selectedVariation?.price ||
		product.price ||
		product.maxRegularPrice ||
		product.minRegularPrice ||
		product.maxPrice ||
		product.minPrice ||
		0;

	const getQuantityOfferSavings = (product, offer, selectedVariation = null) => {
		const basePrice = getQuantityOfferBasePrice(product, selectedVariation);
		const originalTotal = basePrice * offer.quantity;
		return Math.max(originalTotal - offer.packPrice, 0);
	};

	const getPackVariationSelections = (productId, quantity) => {
		const productSelections = selectedQuantityOfferVariations[productId] || {};
		return Array.from({ length: quantity }, (_, index) => productSelections[index] || {});
	};

	const buildProductCaption = (product, selectedVariation = null) => {
		const item = selectedVariation || product;
		const quantityOffers = getQuantityOffers(product, selectedVariation);
		const lines = [`🛍️ *${product.name || "Producto"}*`];

		if (product.sku || item.sku) lines.push(`SKU: ${item.sku || product.sku}`);
		if (product.category) lines.push(`🏷️ *Categoria:* ${product.category}`);
		if (product.description) lines.push("", `📝 *Descripcion:* ${product.description}`);

		const attributes = selectedVariation?.attributes || product.attributes || {};
		const attributeLines = Object.entries(attributes)
			.filter(([, value]) => value)
			.map(([key, value]) => `• *${key}:* ${value}`);
		if (attributeLines.length) lines.push("", "🎯 *Opciones:*", ...attributeLines);

		const price = selectedVariation
			? formatItemPrice(selectedVariation)
			: getProductPriceText(product);
		if (price) lines.push("", `💰 *Precio:* ${price}`);
		lines.push("", "💬 _Responde para consultar disponibilidad_");

		if (quantityOffers.length) {
			lines.splice(
				Math.max(lines.length - 2, 0),
				0,
				"",
				"📦 *Packs disponibles:*",
				formatQuantityOffersText(quantityOffers)
			);
		}

		return lines.filter(line => line !== undefined && line !== null).join("\n");
	};

	const handleVariationChange = (product, attribute, value) => {
		setSelectedVariations(prev => ({
			...prev,
			[product.id]: normalizeVariationSelections(product, {
				...(prev[product.id] || {}),
				[attribute]: value,
			}).selectedAttributes,
		}));
	};

	const handleQuantityOfferSelect = (product, offer) => {
		setSelectedQuantityOffers(prev => ({
			...prev,
			[product.id]: offer.quantity,
		}));
		setSelectedVariations(prev => {
			const next = { ...prev };
			delete next[product.id];
			return next;
		});
		setSelectedQuantityOfferVariations(prev => {
			const current = prev[product.id] || {};
			return {
				...prev,
				[product.id]: Array.from({ length: offer.quantity }).reduce((acc, _item, index) => {
					acc[index] = current[index] || {};
					return acc;
				}, {}),
			};
		});
	};

	const handleQuantityOfferVariationChange = (product, unitIndex, attribute, value) => {
		setSelectedQuantityOfferVariations(prev => {
			const currentProductSelections = prev[product.id] || {};
			const currentUnitSelections = currentProductSelections[unitIndex] || {};
			const normalized = normalizeVariationSelections(product, {
				...currentUnitSelections,
				[attribute]: value,
			}).selectedAttributes;

			return {
				...prev,
				[product.id]: {
					...currentProductSelections,
					[unitIndex]: normalized,
				},
			};
		});
	};

	const handleSendProduct = async product => {
		if (!ticketId) {
			updateStatus("No hay ticket activo para enviar la ficha.", "error");
			return;
		}

		const selectedAttributes = normalizeVariationSelections(
			product,
			selectedVariations[product.id] || {}
		).selectedAttributes;
		const variationOptions = getVariationOptions(product);
		const hasVariationSelectors = Object.keys(variationOptions).length > 0;
		const selectedVariation = findMatchingVariation(product, selectedAttributes);
		const hasAllSelections = Object.keys(variationOptions).every(
			attribute => selectedAttributes[attribute]
		);
		const quantityOffers = getQuantityOffers(product, selectedVariation);
		const selectedOfferQuantity = selectedQuantityOffers[product.id];
		const selectedOffer = quantityOffers.find(offer => offer.quantity === selectedOfferQuantity);

		if (hasVariationSelectors && hasAllSelections && !selectedVariation) {
			updateStatus("La combinacion seleccionada no esta disponible.", "error");
			return;
		}

		if (quantityOffers.length && !selectedOffer) {
			updateStatus("Selecciona un pack para enviar la ficha.", "error");
			return;
		}

		const packVariationSelections = selectedOffer
			? getPackVariationSelections(product.id, selectedOffer.quantity)
			: [];
		if (selectedOffer && hasVariationSelectors) {
			const hasInvalidPackVariation = packVariationSelections.some(selection => {
				const normalized = normalizeVariationSelections(product, selection).selectedAttributes;
				const hasAllPackSelections = Object.keys(variationOptions).every(
					attribute => normalized[attribute]
				);
				return !hasAllPackSelections || !findMatchingVariation(product, normalized);
			});

			if (hasInvalidPackVariation) {
				updateStatus("Selecciona todas las opciones del pack.", "error");
				return;
			}
		}

		setSendingProductId(product.id);
		try {
			await api.post(`/catalog-sheets/products/send/${ticketId}`, {
				productId: product.id,
				selectedAttributes: selectedOffer || !selectedVariation ? {} : selectedAttributes,
				selectedQuantityOffer: selectedOffer
					? {
							quantity: selectedOffer.quantity,
							selectedAttributes: hasVariationSelectors ? packVariationSelections : [],
					  }
					: null,
			});

			toast.success("Ficha del producto enviada.");
			updateStatus(`Ficha enviada: ${product.name}`, "success");
		} catch (err) {
			updateStatus("No se pudo enviar la ficha del producto.", "error");
			toastError(err);
		} finally {
			setSendingProductId("");
		}
	};

	const filteredProducts = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return products;

		return products.filter(product => {
			const values = [
				product.name,
				product.description,
				product.sku,
				product.category,
				product.id,
				...Object.values(product.attributes || {}),
				...(product.variations || []).flatMap(variation => [
					variation.id,
					variation.sku,
					...Object.values(variation.attributes || {}),
				]),
			];
			return values.some(value => String(value || "").toLowerCase().includes(term));
		});
	}, [products, search]);

	const loadConfig = async () => {
		try {
			const { data } = await api.get("/catalog-sheets/config");
			const config = data.config;
			if (!config) return;

			setSpreadsheetId(config.spreadsheetId || "");
			setSheetName(config.sheetName || "");
			setCurrencySymbol(config.currencySymbol || "$");
			await loadProducts();
		} catch (err) {
			toastError(err);
		}
	};

	const loadProducts = async () => {
		setLoading(true);
		updateStatus("Cargando productos...", "info");
		try {
			const { data } = await api.get("/catalog-sheets/products");
			setProducts(data.products || []);
			updateStatus(`${data.products?.length || 0} producto(s) cargados.`, "success");
		} catch (err) {
			setProducts([]);
			updateStatus("No se pudieron cargar los productos del catalogo.", "error");
			if (err?.response?.status !== 404) toastError(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadConfig();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!products.length) {
			setSelectedVariations({});
			return;
		}

		setSelectedVariations(prev => {
			let changed = false;
			const productIds = new Set(products.map(product => String(product.id)));
			const nextSelections = {};

			products.forEach(product => {
				const currentSelections = prev[product.id] || {};
				const normalized = normalizeVariationSelections(product, currentSelections).selectedAttributes;

				if (Object.keys(normalized).length) nextSelections[product.id] = normalized;
				if (!areSelectionsEqual(currentSelections, normalized)) changed = true;
			});

			Object.keys(prev).forEach(productId => {
				if (!productIds.has(String(productId))) changed = true;
			});

			return changed ? nextSelections : prev;
		});
	}, [products]);

	const handleSave = async () => {
		const trimmedSpreadsheetId = spreadsheetId.trim();
		const trimmedSheetName = sheetName.trim();

		if (!trimmedSpreadsheetId || !trimmedSheetName) {
			updateStatus("Completa el ID del documento y la hoja.", "error");
			return;
		}

		try {
			await api.put("/catalog-sheets/config", {
				spreadsheetId: trimmedSpreadsheetId,
				sheetName: trimmedSheetName,
				currencySymbol: currencySymbol.trim() || "$",
			});
			toast.success("Catálogo guardado.");
			await loadProducts();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClear = async () => {
		try {
			await api.delete("/catalog-sheets/config");
			setSpreadsheetId("");
			setSheetName("");
			setCurrencySymbol("$");
			setProducts([]);
			setSearch("");
			updateStatus("Configuracion del catalogo eliminada.", "success");
		} catch (err) {
			toastError(err);
		}
	};

	const statusClass =
		status.type === "success"
			? classes.statusSuccess
			: status.type === "error"
			? classes.statusError
			: "";

	return (
		<Paper square variant="outlined" className={classes.panel}>
			<div className={classes.header}>
				<Typography variant="subtitle1">Catálogo Google Sheets</Typography>
				<div>
					<IconButton
						size="small"
						disabled={loading || !products.length}
						onClick={loadProducts}
						title="Actualizar catalogo"
					>
						<RefreshIcon fontSize="small" />
					</IconButton>
					<IconButton size="small" onClick={handleToggleExpanded}>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</div>
			</div>

			<Collapse in={expanded}>
				<div className={classes.row}>
					<TextField
						label="ID del Google Sheet"
						value={spreadsheetId}
						onChange={event => setSpreadsheetId(event.target.value)}
						variant="outlined"
						margin="dense"
						size="small"
						fullWidth
					/>
					<TextField
						label="Hoja"
						value={sheetName}
						onChange={event => setSheetName(event.target.value)}
						variant="outlined"
						margin="dense"
						size="small"
						style={{ width: 90 }}
					/>
				</div>
				<div className={classes.row}>
					<TextField
						label="Moneda"
						value={currencySymbol}
						onChange={event => setCurrencySymbol(event.target.value.slice(0, 8))}
						variant="outlined"
						margin="dense"
						size="small"
						className={classes.currencyField}
					/>
					<Button variant="contained" color="primary" size="small" onClick={handleSave} disabled={loading}>
						Guardar
					</Button>
					<IconButton
						size="small"
						onClick={() => setClearConfirmOpen(true)}
						title="Limpiar configuracion"
					>
						<DeleteOutlineIcon fontSize="small" />
					</IconButton>
				</div>

				<Typography className={`${classes.status} ${statusClass}`}>
					{loading && <CircularProgress size={12} className={classes.progress} />}
					{status.text}
				</Typography>

				{products.length ? (
					<>
						<Paper square variant="outlined" className={classes.searchWrapper}>
							<SearchIcon className={classes.searchIcon} fontSize="small" />
							<TextField
								value={search}
								onChange={event => setSearch(event.target.value)}
								placeholder="Buscar por nombre, descripcion, SKU o atributo"
								margin="dense"
								size="small"
								fullWidth
							/>
							<Button size="small" onClick={() => setSearch("")}>
								Limpiar
							</Button>
						</Paper>
						<Typography variant="caption" color="textSecondary">
							{filteredProducts.length} de {products.length} producto(s)
						</Typography>
						<Grid container spacing={1}>
							{filteredProducts.map(product => (
								<Grid item xs={12} key={product.id}>
									{(() => {
										const variationOptions = getVariationOptions(product);
										const normalizedSelections = normalizeVariationSelections(
											product,
											selectedVariations[product.id] || {}
										);
										const selectedAttributes = normalizedSelections.selectedAttributes;
										const availableVariationOptions = normalizedSelections.availableOptions;
										const selectedVariation = findMatchingVariation(product, selectedAttributes);
										const hasVariationSelectors = Object.keys(variationOptions).length > 0;
										const hasAllSelections = Object.keys(variationOptions).every(
											attribute => selectedAttributes[attribute]
										);
										const hasInvalidVariation =
											hasVariationSelectors && hasAllSelections && !selectedVariation;
										const displayPrice = selectedVariation
											? formatPrice(selectedVariation)
											: hasInvalidVariation
											? "No disponible"
											: formatPrice(product);
										const quantityOffers = getQuantityOffers(product, selectedVariation);
										const selectedOfferQuantity = selectedQuantityOffers[product.id];
										const selectedOffer = quantityOffers.find(
											offer => offer.quantity === selectedOfferQuantity
										);
										const hasQuantityOffers = quantityOffers.length > 0;
										const sendLabel =
											hasQuantityOffers && selectedOffer
												? "Enviar pack"
												: product.type === "variable" && !selectedVariation
												? "Enviar ficha completa"
												: "Enviar ficha";

										return (
									<Paper square variant="outlined" className={classes.card}>
										<div className={classes.imageBox}>
											<CatalogImage
												src={
													selectedVariation?.image || product.image
												}
												alt={product.name}
												classes={classes}
											/>
										</div>
										<Typography variant="body2" className={classes.productName}>
											{product.name}
										</Typography>
										<div className={classes.chips}>
											{product.sku && <Chip size="small" label={`SKU ${product.sku}`} />}
											{product.category && <Chip size="small" label={product.category} />}
											<Chip size="small" label={product.type === "variable" ? "Variable" : "Simple"} />
										</div>
										{!hasQuantityOffers && (
											<Typography variant="body2" className={classes.price}>
												{displayPrice}
											</Typography>
										)}
										{hasQuantityOffers && (
											<div className={classes.quantityOffers}>
												{quantityOffers.map(offer => {
													const isSelected = selectedOfferQuantity === offer.quantity;
													const savings = getQuantityOfferSavings(product, offer, selectedVariation);
													const packSelections = getPackVariationSelections(product.id, offer.quantity);

													return (
														<Paper
															key={`${offer.quantity}-${offer.packPrice}`}
															square
															variant="outlined"
															className={`${classes.quantityOfferBox} ${
																isSelected ? classes.quantityOfferBoxSelected : ""
															}`}
														>
															<button
																type="button"
																className={classes.quantityOfferButton}
																onClick={() => handleQuantityOfferSelect(product, offer)}
															>
																<div>
																	<div className={classes.quantityOfferTitle}>
																		Lleva {offer.quantity} unidades
																	</div>
																	{savings > 0 && (
																		<span className={classes.quantityOfferSavings}>
																			Ahorra {formatCurrency(savings)}
																		</span>
																	)}
																</div>
																<div className={classes.quantityOfferPrice}>
																	<span className={classes.quantityOfferOldPrice}>
																		{formatCurrency(getQuantityOfferBasePrice(product, selectedVariation) * offer.quantity)}
																	</span>
																	{formatCurrency(offer.packPrice)}
																</div>
															</button>
															{isSelected && product.type === "variable" && (
																<div className={classes.quantityOfferVariations}>
																	{packSelections.map((unitSelections, unitIndex) => {
																		const normalizedUnitSelections = normalizeVariationSelections(
																			product,
																			unitSelections
																		);
																		const unitAttributes = normalizedUnitSelections.selectedAttributes;
																		const unitOptions = normalizedUnitSelections.availableOptions;

																		return (
																			<div
																				key={`${offer.quantity}-${unitIndex}`}
																				className={classes.quantityOfferVariationRow}
																			>
																				<span className={classes.quantityOfferIndex}>
																					{unitIndex + 1}
																				</span>
																				{Object.entries(variationOptions).map(([attribute]) => {
																					const values = unitOptions[attribute] || [];

																					return (
																						<FormControl
																							key={attribute}
																							variant="outlined"
																							margin="dense"
																							size="small"
																							fullWidth
																						>
																							<InputLabel>{attribute}</InputLabel>
																							<Select
																								value={
																									unitAttributes[attribute] ||
																									NO_CATALOG_VARIATION
																								}
																								onChange={event =>
																									handleQuantityOfferVariationChange(
																										product,
																										unitIndex,
																										attribute,
																										event.target.value === NO_CATALOG_VARIATION
																											? ""
																											: event.target.value
																									)
																								}
																								label={attribute}
																							>
																								<MenuItem value={NO_CATALOG_VARIATION}>
																									Seleccionar {attribute}
																								</MenuItem>
																								{values.map(value => (
																									<MenuItem key={value} value={value}>
																										{value}
																									</MenuItem>
																								))}
																							</Select>
																						</FormControl>
																					);
																				})}
																			</div>
																		);
																	})}
																</div>
															)}
														</Paper>
													);
												})}
											</div>
										)}
										{product.description && (
											<Typography variant="body2" color="textSecondary">
												{product.description}
											</Typography>
										)}
										{Object.keys(product.attributes || {}).length > 0 && (
											<Paper square variant="outlined" className={classes.attributes}>
												{Object.entries(product.attributes).map(([key, value]) => (
													<Typography key={key} variant="caption" component="div" className={classes.attributeLine}>
														<strong>{key}:</strong> {value}
													</Typography>
												))}
											</Paper>
										)}
										{!hasQuantityOffers && product.variations?.length > 0 && (
											<Paper square variant="outlined" className={classes.variations}>
												<Typography variant="caption" color="textSecondary">
													Opciones disponibles
												</Typography>
												{Object.entries(variationOptions).map(([attribute]) => {
													const values = availableVariationOptions[attribute] || [];

													return (
													<FormControl
														key={attribute}
														variant="outlined"
														margin="dense"
														size="small"
														fullWidth
														className={classes.variationControl}
													>
														<InputLabel>{attribute}</InputLabel>
														<Select
															value={
																selectedAttributes[attribute] ||
																NO_CATALOG_VARIATION
															}
															onChange={event =>
																handleVariationChange(
																	product,
																	attribute,
																	event.target.value === NO_CATALOG_VARIATION
																		? ""
																		: event.target.value
																)
															}
															label={attribute}
														>
															<MenuItem value={NO_CATALOG_VARIATION}>Seleccionar {attribute}</MenuItem>
															{values.map(value => (
																<MenuItem key={value} value={value}>
																	{value}
																</MenuItem>
															))}
														</Select>
													</FormControl>
													);
												})}
											</Paper>
										)}
										<Button
											variant="contained"
											color="primary"
											size="small"
											startIcon={
												sendingProductId === product.id ? (
													<CircularProgress size={14} color="inherit" />
												) : (
													<SendIcon fontSize="small" />
												)
											}
											disabled={
												Boolean(sendingProductId) ||
												hasInvalidVariation ||
												(hasQuantityOffers && !selectedOffer)
											}
											onClick={() => handleSendProduct(product)}
										>
											{hasInvalidVariation ? "No disponible" : sendLabel}
										</Button>
									</Paper>
										);
									})()}
								</Grid>
							))}
						</Grid>
					</>
				) : (
					<Typography className={classes.empty} variant="body2">
						Configura tu Google Sheet para ver el catalogo de productos.
					</Typography>
				)}
			</Collapse>
			<ConfirmationModal
				title="Limpiar configuracion"
				open={clearConfirmOpen}
				onClose={() => setClearConfirmOpen(false)}
				onConfirm={handleClear}
			>
				¿Seguro que deseas limpiar la configuracion del catalogo?
			</ConfirmationModal>
		</Paper>
	);
};

export default CatalogSheetsPanel;

